import { ReactElement, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/outline';
import Tooltip from 'rc-tooltip';

import { quizscheduleTimeZone } from '../constants';
import {
  ScheduleOverviewQuiz,
  ScheduleOverviewSchool,
  ScheduleOverviewTrack,
} from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);

export type ScheduleGanttZoom = 'months' | 'weeks';

const dayWidths: Record<ScheduleGanttZoom, number> = {
  months: 4,
  weeks: 14,
};

const rowLabelWidth = 224;
const headerHeight = 44;
const rowHeight = 36;
const barHeight = 20;
const minBarWidth = 6;
const cardGap = 2;
const cardLabelMinWidth = 18;

type QuizStatus = 'past' | 'current' | 'upcoming';

type GanttRow =
  | {
      type: 'school';
      key: string;
      school: string;
      collapsed: boolean;
      trackCount: number;
      classCount: number;
    }
  | {
      type: 'class';
      key: string;
      school: string;
      label: string;
      secondaryLabel?: string;
      track: ScheduleOverviewTrack;
    };

type QuizPlacement = {
  quiz: ScheduleOverviewQuiz;
  left: number;
  width: number;
  status: QuizStatus;
};

// Quiz dates are stored as UTC (startDate at midnight, dueDate at 23:59:59) and represent
// calendar days in the schedule timezone. Parse as UTC first so behavior is independent
// of the machine timezone, then normalize to the start of the wall-clock day.
const quizDay = (date: string | Date): dayjs.Dayjs =>
  dayjs.utc(date).tz(quizscheduleTimeZone, true).startOf('day');

// The academic year boundary is calendar-based (Sept 1 in the schedule timezone) and is
// deliberately independent of the backend's `currentAcademicYear` config value.
export const getCurrentAcademicYearStart = (): dayjs.Dayjs => {
  const now = dayjs().tz(quizscheduleTimeZone);
  const year = now.month() >= 8 ? now.year() : now.year() - 1;
  return dayjs
    .utc(`${year}-09-01`)
    .tz(quizscheduleTimeZone, true)
    .startOf('day');
};

export const getQuizStatus = (
  quiz: ScheduleOverviewQuiz,
  today: dayjs.Dayjs,
): QuizStatus => {
  const start = quizDay(quiz.startDate);
  const due = quizDay(quiz.dueDate);
  if (due.isBefore(today)) {
    return 'past';
  }
  if (start.isAfter(today)) {
    return 'upcoming';
  }
  return 'current';
};

export const formatQuizRange = (quiz: ScheduleOverviewQuiz): string => {
  const start = quizDay(quiz.startDate);
  const due = quizDay(quiz.dueDate);
  return `${start.format('MMM D')} - ${due.format('MMM D, YYYY')}`;
};

const statusBackground: Record<QuizStatus, string> = {
  past: 'bg-gray-300',
  current: 'bg-tttDefault',
  upcoming: 'bg-blue-200',
};

const statusTextColor: Record<QuizStatus, string> = {
  past: 'text-gray-700',
  current: 'text-white',
  upcoming: 'text-blue-900',
};

const quizNumber = (quiz: ScheduleOverviewQuiz): string | undefined =>
  quiz.quiz.match(/\d+/)?.[0];

function buildRows(
  schools: ScheduleOverviewSchool[],
  collapsedSchools: Set<string>,
): GanttRow[] {
  const rows: GanttRow[] = [];
  for (const school of schools) {
    const collapsed = collapsedSchools.has(school.school);
    rows.push({
      type: 'school',
      key: `school-${school.school}`,
      school: school.school,
      collapsed,
      trackCount: school.tracks.length,
      classCount: school.tracks.reduce(
        (sum, track) => sum + track.classes.length,
        0,
      ),
    });
    if (collapsed) {
      continue;
    }
    for (const track of school.tracks) {
      if (track.classes.length === 0) {
        rows.push({
          type: 'class',
          key: `track-${track.trackId}`,
          school: school.school,
          label: 'No classes',
          secondaryLabel: track.displayedTrack,
          track,
        });
      }
      for (const clazz of track.classes) {
        rows.push({
          type: 'class',
          key: `class-${clazz.classId}`,
          school: school.school,
          label: `${clazz.className} · ${track.displayedTrack}`,
          secondaryLabel: clazz.teacherName,
          track,
        });
      }
    }
  }
  return rows;
}

export default function ScheduleGantt({
  schools,
  zoom,
  startAt,
}: {
  schools: ScheduleOverviewSchool[];
  zoom: ScheduleGanttZoom;
  /** Overrides the timeline start, e.g. the current academic year start. */
  startAt?: string;
}): ReactElement {
  const [collapsedSchools, setCollapsedSchools] = useState<Set<string>>(
    new Set(),
  );
  const toggleSchool = (school: string) => {
    setCollapsedSchools((previous) => {
      const next = new Set(previous);
      if (next.has(school)) {
        next.delete(school);
      } else {
        next.add(school);
      }
      return next;
    });
  };

  const dayWidth = dayWidths[zoom];
  const today = useMemo(
    () => dayjs().tz(quizscheduleTimeZone).startOf('day'),
    [],
  );
  const rows = useMemo(
    () => buildRows(schools, collapsedSchools),
    [schools, collapsedSchools],
  );

  const { domainStart, domainEnd } = useMemo(() => {
    let start = startAt ? quizDay(startAt) : today.subtract(7, 'day');
    let end = today.add(7, 'day');
    for (const school of schools) {
      for (const track of school.tracks) {
        for (const quiz of track.schedule) {
          const quizStart = quizDay(quiz.startDate);
          const quizDue = quizDay(quiz.dueDate);
          if (start.isAfter(quizStart)) {
            start = quizStart;
          }
          if (end.isBefore(quizDue)) {
            end = quizDue;
          }
        }
      }
    }
    return { domainStart: start, domainEnd: end };
  }, [schools, today, startAt]);

  const totalDays = Math.max(domainEnd.diff(domainStart, 'day') + 1, 1);
  const totalWidth = totalDays * dayWidth;

  const placementsByTrack = useMemo(() => {
    const placements = new Map<string, QuizPlacement[]>();
    for (const school of schools) {
      for (const track of school.tracks) {
        placements.set(
          `${school.school}-${track.trackId}`,
          track.schedule.map((quiz) => {
            const quizStart = quizDay(quiz.startDate);
            const quizDue = quizDay(quiz.dueDate);
            const days = quizDue.diff(quizStart, 'day') + 1;
            return {
              quiz,
              left: quizStart.diff(domainStart, 'day') * dayWidth,
              width: Math.max(minBarWidth, days * dayWidth - cardGap),
              status: getQuizStatus(quiz, today),
            };
          }),
        );
      }
    }
    return placements;
  }, [schools, domainStart, dayWidth, today]);

  const months = useMemo(() => {
    const result: { label: string; left: number; width: number }[] = [];
    let cursor = domainStart.startOf('month');
    while (cursor.isBefore(domainEnd)) {
      const monthStart = cursor;
      const monthEnd = cursor.endOf('month').startOf('day');
      const from = Math.max(0, monthStart.diff(domainStart, 'day'));
      const to = Math.min(totalDays - 1, monthEnd.diff(domainStart, 'day'));
      result.push({
        label: monthStart.format('MMMM YYYY'),
        left: from * dayWidth,
        width: (to - from + 1) * dayWidth,
      });
      cursor = cursor.add(1, 'month').startOf('month');
    }
    return result;
  }, [domainStart, domainEnd, totalDays, dayWidth]);

  const ticks = useMemo(() => {
    const result: { label?: string; left: number }[] = [];
    for (let dayNumber = 0; dayNumber < totalDays; dayNumber++) {
      const date = domainStart.add(dayNumber, 'day');
      if (date.day() === 1) {
        result.push({
          label: zoom === 'weeks' ? date.format('M/D') : undefined,
          left: dayNumber * dayWidth,
        });
      }
    }
    return result;
  }, [domainStart, totalDays, dayWidth, zoom]);

  const todayLeft = today.diff(domainStart, 'day') * dayWidth;
  const todayVisible = todayLeft >= 0 && todayLeft <= totalWidth;
  const gridlineStep = dayWidth * 7;

  return (
    <div
      className="overflow-x-auto rounded-lg border border-gray-200 bg-white"
      data-testid="schedule-gantt"
    >
      <div className="relative" style={{ width: rowLabelWidth + totalWidth }}>
        {/* Header */}
        <div className="sticky top-0 z-20 flex border-b border-gray-200 bg-white">
          <div
            className="sticky left-0 z-30 shrink-0 border-r border-gray-200 bg-white"
            style={{ width: rowLabelWidth, height: headerHeight }}
          >
            <p className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Class · Track
            </p>
          </div>
          <div
            className="relative shrink-0"
            style={{ width: totalWidth, height: headerHeight }}
          >
            {months.map((month) => (
              <div
                key={month.label}
                className="absolute top-0 border-r border-gray-200 pt-1 text-xs font-semibold text-gray-600"
                style={{ left: month.left, width: month.width }}
              >
                {month.width > 42 ? month.label : month.label.slice(0, 3)}
              </div>
            ))}
            {ticks.map((tick, tickIndex) => (
              <div
                key={tickIndex}
                className="absolute bottom-0 border-l border-gray-300 text-[10px] leading-4 text-gray-400"
                style={{ left: tick.left }}
              >
                {tick.label}
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        {rows.map((row) => {
          if (row.type === 'school') {
            return (
              <div
                key={row.key}
                className="flex border-b border-gray-200 bg-gray-100"
                style={{ height: rowHeight }}
              >
                <div
                  className="sticky left-0 z-10 shrink-0 border-r border-gray-200 bg-gray-100"
                  style={{ width: rowLabelWidth }}
                >
                  <div className="flex h-full items-center justify-between gap-2 px-2">
                    <button
                      type="button"
                      onClick={() => toggleSchool(row.school)}
                      className="flex min-w-0 items-center gap-1 rounded text-left"
                      aria-expanded={!row.collapsed}
                      aria-label={`${row.collapsed ? 'Expand' : 'Collapse'} ${
                        row.school
                      }`}
                      data-testid="gantt-school-toggle"
                    >
                      {row.collapsed ? (
                        <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" />
                      )}
                      <span
                        className="truncate text-sm font-semibold text-gray-700"
                        title={row.school}
                        data-testid="gantt-school-header"
                      >
                        {row.school}
                      </span>
                    </button>
                    {row.collapsed && (
                      <span className="shrink-0 text-[10px] text-gray-400">
                        {row.trackCount} tracks · {row.classCount} classes
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0" style={{ width: totalWidth }} />
              </div>
            );
          }

          const placements =
            placementsByTrack.get(`${row.school}-${row.track.trackId}`) ?? [];
          return (
            <div
              key={row.key}
              className="flex border-b border-gray-100"
              style={{ height: rowHeight }}
            >
              <div
                className="sticky left-0 z-10 shrink-0 border-r border-gray-200 bg-white"
                style={{ width: rowLabelWidth }}
              >
                <div className="h-full px-3">
                  <p
                    className="truncate pt-1 text-sm text-gray-800"
                    title={row.label}
                  >
                    {row.label}
                  </p>
                  {row.secondaryLabel && (
                    <p className="truncate text-[10px] leading-3 text-gray-400">
                      {row.secondaryLabel}
                    </p>
                  )}
                </div>
              </div>
              <div
                className="relative shrink-0"
                style={{
                  width: totalWidth,
                  backgroundImage: `repeating-linear-gradient(to right, rgba(0,0,0,0.07) 0 1px, transparent 1px ${gridlineStep}px)`,
                }}
              >
                {row.track.schedule.length === 0 && (
                  <p
                    className="pt-2 pl-2 text-xs italic text-gray-400"
                    data-testid="gantt-no-schedule"
                  >
                    No schedule
                  </p>
                )}
                {placements.map((placement) => {
                  const number = quizNumber(placement.quiz);
                  return (
                    <Tooltip
                      key={placement.quiz.index}
                      placement="top"
                      overlay={
                        <span>
                          <strong>{placement.quiz.quiz}</strong>
                          {placement.quiz.isReview ? ' (review)' : ''}
                          <br />
                          {row.label}
                          <br />
                          {formatQuizRange(placement.quiz)} · {placement.status}
                        </span>
                      }
                    >
                      <div
                        data-testid="gantt-bar"
                        data-quiz={placement.quiz.quiz}
                        data-status={placement.status}
                        aria-label={`${row.label}: ${
                          placement.quiz.quiz
                        }, ${formatQuizRange(placement.quiz)}`}
                        className={`absolute flex items-center justify-center rounded-md border border-white shadow-sm ${
                          statusBackground[placement.status]
                        } ${statusTextColor[placement.status]} ${
                          placement.quiz.isReview ? 'ring-2 ring-amber-400' : ''
                        }`}
                        style={{
                          left: placement.left,
                          width: placement.width,
                          top: (rowHeight - barHeight) / 2,
                          height: barHeight,
                        }}
                      >
                        {placement.width >= cardLabelMinWidth && number && (
                          <span
                            className="select-none text-[10px] font-semibold leading-none"
                            data-testid="gantt-bar-number"
                          >
                            {number}
                          </span>
                        )}
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Today marker */}
        {todayVisible && (
          <div
            className="pointer-events-none absolute bottom-0 z-[5] w-px bg-red-500"
            style={{ left: rowLabelWidth + todayLeft, top: headerHeight }}
            data-testid="gantt-today-line"
          />
        )}
      </div>
    </div>
  );
}
